import { detectPhoneCountry, formatPhoneInput, getPhoneFlag } from '@/utils/phone.utils';
import clsx from 'clsx';
import { type ChangeEvent, useId } from 'react';
import { HintField } from './hint-field';
import type { FieldBlurEvent, FieldChangeEvent } from './field';

type PhoneFieldProps = {
  name?: string;
  label: string;
  value: string;
  error?: string;
  required?: boolean;
  colSpan?: 1 | 2;
  className?: string;
  onChange: (event: FieldChangeEvent) => void;
  onBlur?: (event: FieldBlurEvent) => void;
};

const controlBase =
  'w-full rounded-(--radius-current) bg-transparent px-4 pt-6 pb-2 text-dark-blue outline-none border transition-colors duration-200';

const borderStates = (hasError: boolean) =>
  hasError ? 'border-red-400' : 'border-dark-blue/15 hover:border-dark-blue/30 focus:border-orange';

const labelBase =
  'pointer-events-none absolute left-4 origin-left max-w-[calc(100%-2rem)] truncate transition-all duration-200';

const labelRest = 'top-[17px] text-base text-dark-blue/60';

const labelFloated = 'top-2 text-xs text-dark-blue/60';

export function PhoneField({
  name = 'telephone',
  label,
  value,
  error,
  required,
  colSpan = 1,
  className,
  onChange,
  onBlur,
}: PhoneFieldProps) {
  const inputId = useId();
  const hasError = Boolean(error);
  const isFilled = value.trim() !== '';
  const flag = getPhoneFlag(value);
  const country = detectPhoneCountry(value);

  const handleChange = (event: ChangeEvent<{ name: string; value: string }>) => {
    onChange({
      ...event,
      target: {
        ...event.target,
        name,
        value: formatPhoneInput(event.target.value),
      },
    });
  };

  return (
    <div className={clsx('relative', colSpan === 2 && 'col-span-2', className)}>
      <input
        autoComplete="tel"
        className={clsx(controlBase, borderStates(hasError), 'peer', flag && 'pr-10')}
        id={inputId}
        inputMode="tel"
        name={name}
        placeholder=" "
        type="tel"
        value={value}
        onBlur={onBlur}
        onChange={handleChange}
      />

      {flag ? (
        <span
          aria-label={country ? `Pays détecté : ${country}` : undefined}
          className="pointer-events-none absolute top-[calc(50%+2px)] right-3 -translate-y-1/2 text-sm leading-none opacity-70"
          title={country}
        >
          {flag}
        </span>
      ) : null}

      <label
        htmlFor={inputId}
        className={clsx(
          labelBase,
          isFilled ? labelFloated : labelRest,
          'peer-focus:text-orange peer-focus:top-2 peer-focus:text-xs',
          hasError && 'text-red-500',
        )}
      >
        {label}
        {required && <span className="text-orange ml-0.5">*</span>}
      </label>

      <HintField message={error} variant="error" />
    </div>
  );
}
